/**
 * Debounces an async call, resolving superseded calls with `undefined` rather than leaving their
 * promises pending forever (which would hang whatever is awaiting them).
 *
 * TODO: move this to a shared package. Four packages already ship near-identical debounce helpers
 * (`code-scanning-shared/filter-providers/debounce.ts`, `security-center/overview-dashboard/utils/
 * debounce.ts`, `copilot-agent-plane/routes/debounce.ts`, and `memex/src/client/helpers/
 * debounce-async.ts`), and every one of them leaves superseded promises pending. Consolidating on
 * this implementation should be a deliberate follow-up with its own testing, so it is kept local
 * here rather than folded into any of those.
 *
 * Note `@github-ui/use-debounce` is not a substitute: it is a React hook, and these providers are
 * plain classes instantiated outside of a component render.
 */
export function debounceAsync<A extends unknown[], R>(
  fn: (...args: A) => Promise<R>,
  ms: number,
): (...args: A) => Promise<R | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined
  let supersede: (() => void) | undefined

  return (...args: A) =>
    new Promise<R | undefined>(resolve => {
      clearTimeout(timer)
      supersede?.()
      supersede = () => resolve(undefined)
      timer = setTimeout(() => {
        supersede = undefined
        resolve(fn(...args))
      }, ms)
    })
}
